import React, { createContext, useContext, type ReactNode } from 'react'
import { type SAML2SSOConfig } from '../../config/ssoTypes'

interface SAML2ContextType {
  initiateLogin: () => void
  config: SAML2SSOConfig
}

const SAML2Context = createContext<SAML2ContextType | undefined>(undefined)

interface SAML2ProviderProps {
  config: SAML2SSOConfig
  children: ReactNode
}

export const SAML2Provider: React.FC<SAML2ProviderProps> = ({ config, children }) => {
  const initiateLogin = (): void => {
    // Build SAML request
    const samlRequest = buildSAMLRequest(config)

    // Redirect to IdP
    window.location.href = `${config.idpUrl}?SAMLRequest=${encodeURIComponent(samlRequest)}`
  }

  return (
    <SAML2Context.Provider value={{ initiateLogin, config }}>
      {children}
    </SAML2Context.Provider>
  )
}

export const useSAML2 = (): SAML2ContextType => {
  const context = useContext(SAML2Context)
  if (context === undefined) {
    throw new Error('useSAML2 must be used within SAML2Provider')
  }
  return context
}

// Helper to build SAML request
const buildSAMLRequest = (config: SAML2SSOConfig): string => {
  // SAML request XML structure
  const samlRequest = `
    <samlp:AuthnRequest
      xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol"
      xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion"
      ID="_${generateId()}"
      Version="2.0"
      IssueInstant="${new Date().toISOString()}"
      AssertionConsumerServiceURL="${config.acsUrl}"
      Destination="${config.idpUrl}">
      <saml:Issuer>${config.entityId}</saml:Issuer>
    </samlp:AuthnRequest>
  `

  // Base64 encode
  return btoa(samlRequest)
}

const generateId = (): string => {
  return 'id' + Math.random().toString(36).substring(2, 9)
}

